import { cn } from "@/lib/utils";
import { VgsFieldState, VgsFormState } from "@/hooks/useVgsCollect";
import {
  Control,
  Path,
  UseFormClearErrors,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";
import type { CheckoutFormData } from "./checkout.types";
import { UseQueryResult } from "@tanstack/react-query";
import { CartResponse } from "@/networking/carts.api";
import { CountriesAndLanguages } from "@/networking/addresses.api";
import { useTranslation } from "@/hooks/useTranslation";
import Checkbox from "../ui/input/checkbox";
import AddressForm from "./address-form";
import useCartCountry from "./hooks/useCartCountry";
import useAutocomplete from "./hooks/useAutocomplete";
import { useCallback } from "react";
import { createAddressCountryReset } from "@/lib/address-country-selection";

interface VgsCreditCardFormProps {
  control?: Control<CheckoutFormData>;
  setValue?: UseFormSetValue<CheckoutFormData>;
  watch?: UseFormWatch<CheckoutFormData>;
  clearErrors?: UseFormClearErrors<CheckoutFormData>;
  query?: UseQueryResult<CartResponse | null>;
  countries?: CountriesAndLanguages["countries"];
  cartToken?: string;
  paymentMethodsIncludes?: string | null;
  isFormReady: boolean;
  iframeLoadError?: boolean;
  isLast?: boolean;
  vgsFieldState: VgsFormState;
  networkError?: string;
}

const VgsInputWrapper = ({
  id,
  label,
  fieldState,
  className,
  children,
}: {
  id: string;
  label: string;
  fieldState?: VgsFieldState;
  className?: string;
  children?: React.ReactNode;
}) => {
  const isInvalid =
    fieldState?.isValid === false &&
    !fieldState?.isFocused &&
    !fieldState?.isEmpty;

  const shouldFloatLabel = fieldState?.isFocused || !fieldState?.isEmpty;
  const isFocused = fieldState?.isFocused;

  return (
    <div
      className={cn(
        "relative w-full bg-white transition-all duration-200 ease-in-out",
        isFocused && "z-20",
      )}
    >
      <div
        id={id}
        className={cn(
          "h-input w-full px-3 pt-4 [&_iframe]:!block [&_iframe]:!h-full [&_iframe]:!w-full",
          isInvalid ? "shadow-error" : "ring-1 ring-gray-300",
          isFocused && "ring-2 ring-black ring-inset",
          className,
        )}
      />

      <span
        className={cn(
          "pointer-events-none absolute left-3 z-10 transform text-gray-400 transition-all duration-200",
          shouldFloatLabel
            ? "top-1 text-xs"
            : "top-1/2 -translate-y-1/2 text-sm",
          isInvalid && "text-red-400",
        )}
      >
        {label}
      </span>

      {children}
    </div>
  );
};

export default function VgsCreditCardForm({
  control,
  setValue,
  watch,
  clearErrors,
  query,
  countries,
  cartToken,
  paymentMethodsIncludes,
  isFormReady,
  iframeLoadError,
  isLast,
  vgsFieldState,
  networkError,
}: VgsCreditCardFormProps) {
  const { t } = useTranslation();

  const useShippingForBilling = watch
    ? (watch("customer.payment_method.use_shipping_address_for_billing") ??
      false)
    : false;

  const billingFieldPath =
    "customer.payment_method.billing_address" as Path<CheckoutFormData>;

  const {
    stateOptions: billingStateOptions,
    addressFields: billingAddressFields,
    handleCountryChange: billingHandleCountryChange,
  } = useCartCountry(
    cartToken || "",
    paymentMethodsIncludes || null,
    query?.data?.cart?.country?.iso,
    countries,
  );

  const {
    renderAutocompleteOptions: billingRenderAutocompleteOptions,
    getAutocompleteOptions: billingGetAutocompleteOptions,
    handleOptionClick: billingHandleOptionClick,
  } = useAutocomplete(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setValue as any,
    `${billingFieldPath}.city` as Path<CheckoutFormData>,
    `${billingFieldPath}.state` as Path<CheckoutFormData>,
    `${billingFieldPath}.postal_code` as Path<CheckoutFormData>,
    `${billingFieldPath}.address1` as Path<CheckoutFormData>,
    billingStateOptions,
  );

  const onBillingCountryChange = useCallback(
    (countryCode: string, countryName: string) => {
      if (!setValue) return;

      const resetData = createAddressCountryReset(countryCode, countryName);

      Object.entries(resetData).forEach(([key, value]) => {
        setValue(
          `${billingFieldPath}.${key}` as Path<CheckoutFormData>,
          value as string,
          { shouldValidate: false },
        );
      });
      billingHandleCountryChange(countryCode, false);
    },
    [setValue, billingFieldPath, billingHandleCountryChange],
  );

  if (iframeLoadError) {
    return (
      <div className="rounded-b bg-white p-8 shadow-xs ring-1 ring-gray-300">
        <div className="flex flex-col items-center justify-center gap-2 text-center">
          <p className="text-sm font-medium text-red-600">
            {t("payment_form_load_failed")}
          </p>
          <p className="text-xs text-gray-500">
            {t("payment_form_load_failed_hint")}
          </p>
        </div>
      </div>
    );
  }

  if (!isFormReady) {
    return (
      <div className="rounded-b bg-white p-8 shadow-xs ring-1 ring-gray-300">
        <div className="flex items-center justify-center">
          <div className="mr-3 h-6 w-6 animate-spin rounded-full border-4 border-t-4 border-blue-500 border-t-transparent" />
          <p className="text-sm text-gray-600">
            {t("loading_secure_payment_form")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "rounded bg-white shadow-xs",
        isLast && useShippingForBilling && "rounded-b",
      )}
      aria-label={t("credit_card_form")}
    >
      <VgsInputWrapper
        id="vgs-card-number"
        label={t("card_number")}
        fieldState={vgsFieldState["card_number"]}
        className={cn(!control && "rounded-t", "border-b-0")}
      />

      <div className="sm:flex">
        <div className="relative min-w-0 flex-1">
          <VgsInputWrapper
            id="vgs-expiration-date"
            label={t("expiration_date_label")}
            fieldState={vgsFieldState["card_exp"]}
            className="rounded-none border-b-0 sm:border-r-0"
          />
        </div>
        <div className="relative min-w-0 flex-1">
          <VgsInputWrapper
            id="vgs-cvc"
            label={t("cvc")}
            fieldState={vgsFieldState["card_cvc"]}
            className="rounded-none border-b-0"
          />
        </div>
      </div>

      <div className="relative w-full">
        <VgsInputWrapper
          id="vgs-first-name"
          label={t("cardholder_name")}
          fieldState={vgsFieldState["card_holder"]}
          className={cn(
            query?.data?.cart?.digital_only && isLast
              ? "rounded-b"
              : "rounded-none",
          )}
        />
      </div>

      {networkError && (
        <div className="border-t border-gray-300 bg-red-50 px-4 py-3 text-sm text-red-600">
          {networkError}
        </div>
      )}

      {!query?.data?.cart?.digital_only &&
        control &&
        setValue &&
        clearErrors && (
          <div
            className={cn(
              "h-input flex w-full items-center px-4 ring-1 ring-gray-300",
              !useShippingForBilling
                ? "rounded-none"
                : isLast
                  ? "rounded-b"
                  : "rounded-none",
            )}
          >
            <Checkbox
              control={control}
              name="customer.payment_method.use_shipping_address_for_billing"
              label={t("use_shipping_address_for_billing")}
              id="use-shipping-address-for-billing"
              onChange={() => {
                setValue(
                  "customer.payment_method.use_shipping_address_for_billing",
                  !useShippingForBilling,
                );
                clearErrors("customer.payment_method.billing_address");
              }}
            />
          </div>
        )}

      {!query?.data?.cart?.digital_only &&
        !useShippingForBilling &&
        control &&
        countries &&
        watch && (
          <AddressForm
            control={control}
            countries={countries}
            watch={watch}
            fieldPath={billingFieldPath}
            stateOptions={billingStateOptions}
            addressFields={billingAddressFields}
            onCountryChange={onBillingCountryChange}
            renderAutocompleteOptions={billingRenderAutocompleteOptions}
            getAutocompleteOptions={billingGetAutocompleteOptions}
            handleOptionClick={billingHandleOptionClick}
            isLoading={query?.isLoading || false}
            roundedTop={false}
            roundedBottom={isLast}
            className="mt-[-7px] mb-[-7px]"
            collectName={false}
            clearErrors={clearErrors}
          />
        )}
    </div>
  );
}
